import { useState, type FocusEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { LuRadio } from "react-icons/lu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { WeekDayCycler } from "@/components/v2/home/WeekDayCycler";
import { CycleSharePanel } from "@/components/v2/home/CycleSharePanel";
import { MAX_POLL_DAYS, type CreatePollV2Input } from "@/lib/api/polls";
import { dayKeyFromDate } from "@/lib/v2/pollAggregates";
import { useMediaQuery } from "@/hooks/use-media-query";

export interface PollCreateShareResult {
  shareUrl: string;
  sharePassword: string;
}

interface PollCreationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  availability: Record<string, number>;
  creating: boolean;
  defaultQuestion?: string;
  groupName?: string;
  onConfirm: (input: CreatePollV2Input) => Promise<PollCreateShareResult | null>;
}

type DialogStep = "configure" | "share";

function scrollMobileInputIntoView(event: FocusEvent<HTMLInputElement>) {
  requestAnimationFrame(() => {
    event.target.scrollIntoView({ block: "center", behavior: "smooth" });
  });
}

function PollCreationShell({
  mobile,
  open,
  onOpenChange,
  children,
  footer,
}: {
  mobile: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  if (mobile) {
    return (
      <Drawer
        open={open}
        onOpenChange={onOpenChange}
        fixed
        repositionInputs={false}
        shouldScaleBackground={false}
      >
        <DrawerContent className="mt-0 flex max-h-[96dvh] flex-col px-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch]">
            {children}
          </div>
          {footer ? (
            <DrawerFooter className="shrink-0 border-t border-border/60 bg-background px-0 pb-0 pt-3">
              {footer}
            </DrawerFooter>
          ) : null}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
        {children}
        {footer ? <DialogFooter className="gap-2 sm:gap-0">{footer}</DialogFooter> : null}
      </DialogContent>
    </Dialog>
  );
}

function PollCreationHeader({
  mobile,
  title,
  description,
}: {
  mobile: boolean;
  title: string;
  description: string;
}) {
  if (mobile) {
    return (
      <DrawerHeader className="px-0 text-left">
        <DrawerTitle>{title}</DrawerTitle>
        <DrawerDescription>{description}</DrawerDescription>
      </DrawerHeader>
    );
  }

  return (
    <DialogHeader>
      <DialogTitle>{title}</DialogTitle>
      <DialogDescription>{description}</DialogDescription>
    </DialogHeader>
  );
}

export function PollCreationDialog({
  open,
  onOpenChange,
  availability,
  creating,
  defaultQuestion,
  groupName,
  onConfirm,
}: PollCreationDialogProps) {
  const { t } = useTranslation();
  const isMobile = useMediaQuery("(max-width: 768px)");

  const [step, setStep] = useState<DialogStep>("configure");
  const [question, setQuestion] = useState(
    defaultQuestion ?? t("v2.home.pollDefaultQuestion")
  );
  const [selectedDays, setSelectedDays] = useState<Date[]>([]);
  const [publicLink, setPublicLink] = useState(true);
  const [linkPassword, setLinkPassword] = useState("");
  const [shareResult, setShareResult] = useState<PollCreateShareResult | null>(null);

  const reset = () => {
    setStep("configure");
    setQuestion(defaultQuestion ?? t("v2.home.pollDefaultQuestion"));
    setSelectedDays([]);
    setPublicLink(true);
    setLinkPassword("");
    setShareResult(null);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const canConfirm =
    selectedDays.length > 0 && (!publicLink || linkPassword.trim().length >= 4);

  const handleConfirm = async () => {
    const sorted = [...selectedDays].sort((a, b) => a.getTime() - b.getTime());
    const result = await onConfirm({
      question,
      mode: "days_only",
      days: sorted.map((d) => dayKeyFromDate(d)),
      publicPassword: publicLink ? linkPassword.trim() : undefined,
    });

    if (result) {
      setShareResult(result);
      setStep("share");
      return;
    }

    handleOpenChange(false);
  };

  const configureFooter = (
    <>
      <Button
        variant="outline"
        onClick={() => handleOpenChange(false)}
        className="min-h-[48px] w-full sm:w-auto"
      >
        {t("common.cancel")}
      </Button>
      <Button
        disabled={!canConfirm || creating}
        onClick={handleConfirm}
        className="min-h-[48px] w-full bg-soccer-primary font-bold text-black hover:bg-soccer-primary/90 sm:w-auto"
      >
        <LuRadio className="h-4 w-4" />
        {creating ? t("common.loading") : t("v2.home.openPollButton")}
      </Button>
    </>
  );

  return (
    <PollCreationShell
      mobile={isMobile}
      open={open}
      onOpenChange={handleOpenChange}
      footer={step === "configure" ? configureFooter : undefined}
    >
      {step === "configure" ? (
        <>
          <PollCreationHeader
            mobile={isMobile}
            title={t("v2.home.pollCreationTitle")}
            description={t("v2.home.pollCreationDescDaysOnly", { max: MAX_POLL_DAYS })}
          />

          <div className="space-y-5 py-1">
            <div className="space-y-2">
              <Label htmlFor="poll-question">{t("v2.home.pollQuestionLabel")}</Label>
              <Input
                id="poll-question"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onFocus={isMobile ? scrollMobileInputIntoView : undefined}
                placeholder={t("v2.home.pollDefaultQuestion")}
                className="min-h-[44px] text-base md:text-sm"
              />
            </div>

            <div className="space-y-2">
              <Label>
                {t("v2.home.proposedDaysLabel", {
                  count: selectedDays.length,
                  max: MAX_POLL_DAYS,
                })}
              </Label>
              <p className="text-sm text-muted-foreground">
                {t("v2.home.pollCreationDaysOnlyHint")}
              </p>
              <WeekDayCycler
                resetKey={open}
                selected={selectedDays}
                onChange={setSelectedDays}
                max={MAX_POLL_DAYS}
                showAvailability
                availability={availability}
                compact
                hint={t("v2.home.proposedDaysHint")}
              />
            </div>

            <Separator />

            <div className="space-y-3">
              <div className="flex items-center justify-between gap-3 rounded-lg border bg-muted/30 px-3 py-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{t("v2.home.publicLinkToggle")}</p>
                  <p className="text-sm text-muted-foreground">
                    {t("v2.home.publicLinkToggleDesc")}
                  </p>
                </div>
                <Switch checked={publicLink} onCheckedChange={setPublicLink} />
              </div>
              {publicLink && (
                <div className="space-y-2">
                  <Label htmlFor="link-password">{t("v2.home.linkPasswordLabel")}</Label>
                  <Input
                    id="link-password"
                    type="password"
                    value={linkPassword}
                    onChange={(e) => setLinkPassword(e.target.value)}
                    onFocus={isMobile ? scrollMobileInputIntoView : undefined}
                    placeholder={t("v2.home.linkPasswordPlaceholder")}
                    className="min-h-[44px] text-base md:text-sm"
                  />
                </div>
              )}
            </div>
          </div>
        </>
      ) : (
        <>
          <PollCreationHeader
            mobile={isMobile}
            title={t("v2.home.pollShareStepTitle")}
            description={t("v2.home.pollSharePromptDescReady")}
          />

          {shareResult && (
            <CycleSharePanel
              ready
              shareUrl={shareResult.shareUrl}
              sharePassword={shareResult.sharePassword}
              groupName={groupName}
              settingUp={false}
              messageVariant="poll"
              onSetupPassword={async () => null}
              onDone={() => handleOpenChange(false)}
            />
          )}
        </>
      )}
    </PollCreationShell>
  );
}
